import type { UIMessage } from 'ai';

export const SESSIONS_STORAGE_KEY = 'liftai.otto.sessions:v2';
export const LEGACY_SESSIONS_STORAGE_KEY = 'liftai.intelligence.sessions:v1';
export const MAX_STORED_SESSIONS = 20;

export type StoredSession = {
  id: string;
  title: string;
  createdAt: number;
  messages: UIMessage[];
  lastAssistantAt: number | null;
  // Single source of truth for the "ready" badge. Persisted so that a
  // refresh restores exactly the unread state the user saw before.
  unread: boolean;
};

export type StoredV2 = {
  version: 2;
  activeSessionId: string | null;
  sessionOrder: string[];
  sessions: Record<string, StoredSession>;
};

const EMPTY: StoredV2 = {
  version: 2,
  activeSessionId: null,
  sessionOrder: [],
  sessions: {},
};

function safeRead(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function readStored(): StoredV2 {
  const parsed = safeRead(SESSIONS_STORAGE_KEY);
  if (!parsed || typeof parsed !== 'object') return { ...EMPTY };
  const p = parsed as Partial<StoredV2>;
  if (p.version !== 2 || !Array.isArray(p.sessionOrder) || !p.sessions) {
    return { ...EMPTY };
  }
  // Normalise stored sessions: older snapshots (pre-`unread`) used a
  // lastViewedAt timestamp; treat them all as read so refreshing the page
  // doesn't resurrect badges for chats the user already opened.
  const rawSessions = p.sessions as Record<string, Partial<StoredSession>>;
  const sessions: Record<string, StoredSession> = {};
  for (const [id, raw] of Object.entries(rawSessions)) {
    if (!raw || typeof raw !== 'object') continue;
    sessions[id] = {
      id: typeof raw.id === 'string' ? raw.id : id,
      title: typeof raw.title === 'string' ? raw.title : 'New chat',
      createdAt: typeof raw.createdAt === 'number' ? raw.createdAt : 0,
      messages: Array.isArray(raw.messages) ? raw.messages : [],
      lastAssistantAt: typeof raw.lastAssistantAt === 'number' ? raw.lastAssistantAt : null,
      unread: typeof raw.unread === 'boolean' ? raw.unread : false,
    };
  }
  return {
    version: 2,
    activeSessionId: typeof p.activeSessionId === 'string' ? p.activeSessionId : null,
    sessionOrder: p.sessionOrder.filter((id): id is string => typeof id === 'string'),
    sessions,
  };
}

export function writeStored(state: StoredV2): boolean {
  try {
    const order = state.sessionOrder.slice(0, MAX_STORED_SESSIONS);
    const sessions: Record<string, StoredSession> = {};
    for (const id of order) {
      const s = state.sessions[id];
      if (s) sessions[id] = s;
    }
    const activeSessionId =
      state.activeSessionId && order.includes(state.activeSessionId) ? state.activeSessionId : null;
    const payload: StoredV2 = { version: 2, activeSessionId, sessionOrder: order, sessions };
    localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(payload));
    return true;
  } catch {
    // Quota or storage disabled — caller may retry; chat still works in-memory.
    return false;
  }
}

export function makeSessionTitle(messages: UIMessage[]): string {
  const firstUser = messages.find((m) => m.role === 'user');
  if (!firstUser) return 'New chat';
  const text = firstUser.parts
    .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
    .map((p) => p.text)
    .join(' ')
    .trim();
  if (!text) return 'New chat';
  return text.length > 60 ? `${text.slice(0, 60)}…` : text;
}

export function isSessionReady(session: StoredSession): boolean {
  return session.unread;
}

type LegacyV1 = {
  id: string;
  title: string;
  createdAt: number;
  messages: UIMessage[];
};

export function migrateLegacyIfPresent(): StoredV2 | null {
  const parsed = safeRead(LEGACY_SESSIONS_STORAGE_KEY);
  if (parsed == null || !Array.isArray(parsed)) {
    // Missing key, malformed JSON, or wrong shape. `removeItem` is a
    // no-op when the key is already absent, so this is safe to call
    // unconditionally.
    localStorage.removeItem(LEGACY_SESSIONS_STORAGE_KEY);
    return null;
  }
  // The legacy key can re-appear after v2 has been populated — a
  // rollback to the old /intelligence route, or a second tab still
  // running the old build, will rewrite it. Migrating blindly would
  // overwrite the live Otto sessions in v2 with a stale v1 snapshot
  // (or even with an empty list, if the stale v1 was already cleared).
  // When v2 already has sessions, treat the legacy key as obsolete:
  // drop it and let readStored take over.
  if (readStored().sessionOrder.length > 0) {
    localStorage.removeItem(LEGACY_SESSIONS_STORAGE_KEY);
    return null;
  }
  const now = Date.now();
  const order: string[] = [];
  const sessions: Record<string, StoredSession> = {};
  for (const raw of parsed as LegacyV1[]) {
    if (!raw || typeof raw.id !== 'string') continue;
    order.push(raw.id);
    sessions[raw.id] = {
      id: raw.id,
      title: typeof raw.title === 'string' ? raw.title : 'New chat',
      createdAt: typeof raw.createdAt === 'number' ? raw.createdAt : now,
      messages: Array.isArray(raw.messages) ? raw.messages : [],
      lastAssistantAt: typeof raw.createdAt === 'number' ? raw.createdAt : now,
      unread: false,
    };
  }
  const migrated: StoredV2 = {
    version: 2,
    activeSessionId: null,
    sessionOrder: order,
    sessions,
  };
  if (!writeStored(migrated)) {
    return migrated;
  }
  localStorage.removeItem(LEGACY_SESSIONS_STORAGE_KEY);
  return migrated;
}

export function relativeTime(timestamp: number, now: number = Date.now()): string {
  const diff = Math.max(0, now - timestamp);
  if (diff < 60_000) return 'Just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins} min ago`;
  if (diff < 24 * 3600_000) return 'Earlier today';
  if (diff < 48 * 3600_000) return 'Yesterday';
  const days = Math.floor(diff / 86_400_000);
  if (days < 7) return `${days} days ago`;
  if (days < 14) return 'Last week';
  return `${Math.floor(days / 7)} weeks ago`;
}
